import type {
    Garage,
    GarageLaterAvailability,
    LaterAvailabilityOption,
    LaterAvailabilityTime,
} from '../types'
import type { GarageLaterAvailabilityData } from './types'

const LATER_START_OPTIONS_BY_SEARCH_TIME = {
    '12:00': ['16:00', '17:00', '18:00'],
    '13:00': ['16:00', '17:00', '18:00'],
    '14:00': ['16:00', '17:00', '18:00'],
    '15:00': ['16:00', '17:00', '18:00'],
    '16:00': ['17:00', '18:00'],
    '17:00': ['18:00'],
} as const satisfies Record<string, ReadonlyArray<LaterAvailabilityTime>>

type LaterAvailabilitySearchTime = keyof typeof LATER_START_OPTIONS_BY_SEARCH_TIME

const isLaterAvailabilitySearchTime = (
    searchStart: string | null | undefined,
): searchStart is LaterAvailabilitySearchTime =>
    typeof searchStart === 'string' &&
    Object.prototype.hasOwnProperty.call(LATER_START_OPTIONS_BY_SEARCH_TIME, searchStart)

export const getLaterStartOptions = (
    searchStart: string | null | undefined,
): Array<LaterAvailabilityTime> => isLaterAvailabilitySearchTime(searchStart)
    ? [...LATER_START_OPTIONS_BY_SEARCH_TIME[searchStart]]
    : []

const normalizeGarageLaterAvailability = (
    garage: GarageLaterAvailabilityData,
    candidateStartTimes: ReadonlyArray<LaterAvailabilityTime>,
): GarageLaterAvailability | null => {
    const timeByHour = new Map(
        candidateStartTimes.map(time => [Number.parseInt(time, 10), time]),
    )
    const availableTimes = Array.from(new Set(garage.available_at))
        .map(hour => timeByHour.get(hour))
        .filter((time): time is LaterAvailabilityTime => Boolean(time))
        .sort((a, b) => a.localeCompare(b))
    const earliestAvailableTime = availableTimes[0]
    if (!earliestAvailableTime) return null

    return {
        garageId: garage.id,
        availableTimes,
        earliestAvailableTime,
    }
}

export const getGarageLaterAvailability = (
    garageId: string | number | undefined,
    availabilityData: Array<GarageLaterAvailabilityData>,
    searchedStartTime: string | null | undefined,
): GarageLaterAvailability | null => {
    const numericGarageId = Number(garageId)
    const garage = availabilityData.find(item => item.id === numericGarageId)
    return garage
        ? normalizeGarageLaterAvailability(garage, getLaterStartOptions(searchedStartTime))
        : null
}

export type LaterAvailabilitySummary = {
    garages: Array<GarageLaterAvailability>,
    options: Array<LaterAvailabilityOption>,
}

export const deriveLaterAvailability = (
    garageList: Array<Garage>,
    availabilityData: Array<GarageLaterAvailabilityData>,
    searchedStartTime: string | null | undefined,
): LaterAvailabilitySummary => {
    const candidateStartTimes = getLaterStartOptions(searchedStartTime)
    const unavailableGarageIds = new Set(
        garageList
            .filter(garage => garage.checkLaterAvailability && garage.timeRangeValid === true && !garage.available)
            .map(garage => garage.id),
    )

    const garages = availabilityData.flatMap<GarageLaterAvailability>(garage => {
        if (!unavailableGarageIds.has(garage.id)) return []

        const laterAvailability = normalizeGarageLaterAvailability(garage, candidateStartTimes)
        return laterAvailability ? [laterAvailability] : []
    })

    const listedGarageIds = new Set(garageList.map(garage => garage.id))
    const currentlyAvailableIds = new Set(garageList.filter(garage => garage.available).map(garage => garage.id))
    const options = candidateStartTimes.flatMap<LaterAvailabilityOption>(startTime => {
        const availableIds = new Set(availabilityData
            .filter(garage => listedGarageIds.has(garage.id) && garage.available_at.includes(Number.parseInt(startTime, 10)))
            .map(garage => garage.id))
        const diff = availableIds.size - currentlyAvailableIds.size

        if (diff <= 0) return []

        return [{
            startTime,
            garageIds: [...availableIds].filter(id => !currentlyAvailableIds.has(id)),
            diff,
        }]
    })

    return {
        garages,
        options,
    }
}
